<template>
  <div>
    <ServicesHeroPortal
      v-show="!isAuthenticated"
      :title="general.title"
      :icon="general.icon"
      :sub-title="general.subTitle"
      :list-items="general.listItems"
      :images="general.images"
      :services="services"
    />
    <div
      class="mx-auto flex w-full max-w-content-container flex-col justify-center gap-y-7 px-small pb-20 md:items-start xl:pb-36 xl:pl-16 xl:pr-0"
      :class="{
        'pt-8 xl:pt-10': isAuthenticated,
      }"
    >
      <div v-show="isAuthenticated">
        <Breadcrumb :menu="breadcrumbStore.items" />
      </div>

      <div class="relative h-full w-full">
        <SectionTitle
          v-show="isAuthenticated"
          data-testid="portal-title"
          class="mb-6 ml-0 lg:ml-5"
          :title="general.title"
          :icon="general.icon"
          :mineral="general.category"
          authorized
        />
        <div class="mt-5 pb-0 lg:mt-8">
          <div class="flex grid-cols-5 flex-col gap-x-3 gap-y-6 xl:grid">
            <div class="col-span-5 flex grid-cols-2 flex-col gap-2.5 xl:grid">
              <div
                class="flex min-h-[220px] w-full animate-pulse items-center justify-center rounded-xl bg-s-300 xl:!min-h-[274px] xl:min-h-[381px]"
                :class="{
                  '!animate-none': !loadingArticles,
                }"
              >
                <CarouselArticle
                  v-if="articles.length"
                  :market="general.slug"
                  :articles="articles"
                  class="w-full xl:h-[274px]"
                />
              </div>
              <div
                class="relative h-[181px] grow rounded-xl bg-s-300 md:h-60 xl:h-auto"
              >
                <CarouselCapexTracker
                  class="!relative rounded-xl [&_img]:rounded-xl"
                  button-classes="!flex bg-white !text-black border-2"
                  :material="ALL_MATERIALS_TOTALS.mineral"
                  :value="ALL_MATERIALS_TOTALS.value"
                  :image-url="ALL_MATERIALS_TOTALS.imageUrl"
                />
              </div>
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <div class="mb-[58px] mt-16 w-full">
              <div
                v-if="loadingReports"
                class="flex h-[444px] w-full items-center justify-center"
              >
                <TheLoader />
              </div>
              <ReportsAndModelsSection
                v-else
                :key="`reports-and-models-${jwtToken}`"
                :reports="reportCards"
                :product="general.slug"
                :category="general.categoryReportPricing"
              />
            </div>
          </div>

          <div class="relative mt-5 w-full max-w-content-container">
            <VideoSection :videos="videos" :product-slug="general.slug" />
          </div>
          <div class="mt-8 w-full max-w-content-container">
            <EventSection />
          </div>
        </div>
      </div>
    </div>
    <PdfViewerModal :url="pdfUrl" @close="closeViewer" />
  </div>
</template>

<script setup lang="ts">
import CarouselCapexTracker from '~/components/carousel/CapexTracker.vue'
import PdfViewerModal from '~/components/critical-insights/PdfViewerModal.vue'
import CarouselArticle from '~/components/market-insights/CarouselArticle.vue'
import EventSection from '~/components/portals/EventSection.vue'
import ReportsAndModelsSection from '~/components/portals/ReportsAndModelsSection.vue'
import VideoSection from '~/components/portals/VideoSection.vue'
import SectionTitle from '~/components/price-assessments/SectionTitle.vue'
import Breadcrumb from '~/components/shared/navigation/Breadcrumb.vue'
import ServicesHeroPortal from '~/components/shared/services/HeroPortal.vue'
import useAuthentication from '~/composables/useAuthentication'
import { useReports } from '~/composables/useReports'
import { useSeo } from '~/composables/useSeo'
import { ALL_MATERIALS_TOTALS } from '~/constants/capex-tracker-data'
import copperPortalData from '~/constants/portal/copper'
import { getServices } from '~/constants/services'
import { COPPER } from '~/constants/services/constants'
import { MARKET_TYPE } from '~/src/constants/markets'
import type { TReportCard } from '~/src/types/reports'
import useAuthStore from '~/stores/auth'
import { useBreadcrumbStore } from '~/stores/breadcrumb'
import { useModalStore } from '~/stores/modal'
import { reformat } from '~/utils/date'
import { logError } from '~/utils/log-error'

const general = {
  ...COPPER,
  ...copperPortalData,
  title: COPPER.name,
  icon: COPPER.iconColor,
  categoryAnalysis: COPPER.slug,
} as const

const { $graphqlClient } = useNuxtApp()
const { getReportAndModelData } = useReports()
const { getVideos, getEvents } = usePortal()
const authStore = useAuthStore()
const breadcrumbStore = useBreadcrumbStore()
const modalStore = useModalStore()
const { modalParams } = storeToRefs(modalStore)

const services = ref({ checkboxes: [] })
const articles = ref([])
const videos = ref([])
const events = ref([])
const loadingArticles = ref(true)
const reportCards = ref<TReportCard[]>([])
const loadingReports = ref(true)

const pdfUrl = computed(() => modalParams.value?.pdfUrl)
const isAuthenticated = computed(() => authStore.authenticated)
const jwtToken = computed(() =>
  authStore.jwtToken ? 'with-token' : 'without-token',
)

const fetchReports = async () => {
  try {
    const marketTypes = [MARKET_TYPE.FORECASTS, MARKET_TYPE.WEEKLY_REPORTS]
    const cards = await getReportAndModelData(general.slug, marketTypes)
    reportCards.value = cards.map((card) => ({
      ...card,
      assessment_type: null,
    }))
    loadingReports.value = false
  } catch (error) {
    logError(error)
  } finally {
    loadingReports.value = false
  }
}

const getCriticalInsights = async () => {
  const reports = await getReportAndModelData(
    general.slug,
    [MARKET_TYPE.CRITICAL_INSIGHTS],
    true,
    1,
  )
  return reports.map((report) => ({
    ...report,
    backgroundImage: report.images.desktop,
    publishedDate: reformat(report.date, 'dd MMM yyyy'),
  }))
}

const fetchArticles = async () => {
  loadingArticles.value = true
  try {
    const [portalArticles, criticalInsights] = await Promise.all([
      $graphqlClient.getPortalArticles({
        market: general.slug,
        categories: null,
      }),
      getCriticalInsights(),
    ])
    articles.value = portalArticles.articles
      .toSpliced(2, 0, ...criticalInsights)
      .slice(0, 5)
  } catch (error) {
    logError(error)
  } finally {
    loadingArticles.value = false
  }
}

const closeViewer = () => {
  modalStore.setModalParams({ pdfUrl: '' })
}

onMounted(async () => {
  services.value = getServices([general.slug])

  try {
    const responses = await Promise.all([
      getVideos({
        slug: general.slug,
        categories: [general.slug],
      }),
      getEvents(),
      fetchReports(),
      fetchArticles(),
    ])
    videos.value = responses[0]
    events.value = responses[1]
  } catch (error) {
    logError(error)
  }
})

const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: 'Copper Market Developments & Industry Updates',
  description:
    'Benchmark provides essential copper market insights, weekly reports, and event details to guide strategic decision making across the supply chain.',
})

useAuthentication(async () => {
  try {
    const responses = await Promise.all([
      getVideos({
        slug: general.slug,
        categories: [general.slug],
      }),
      fetchReports(),
      fetchArticles(),
    ])
    videos.value = responses[0]
  } catch (error) {
    logError(error)
  }
})

useHead(seo)
definePageMeta({ layout: 'services' })
</script>
